import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import CustomAppBar from "../../components/AppBar";
import { useTranslation } from "react-i18next";
import GenericButton from "../../components/Custom/GenericButton";
import OrderCard from "../../components/Biochar/OrderCard";
import OrderDetailsDialog from "../../components/Biochar/OrderDetailsDialog";
import api from "../../globals/axios";
import { useAlert } from "../../components/Alert";
import OrderDesktopCard from "../../components/Biochar/OrderDesktopCard";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import { sharePaymentLink } from "../../utils/BiocharOrder";

const BiocharSaleOrders = () => {
  const { t } = useTranslation();
  const { showAlert } = useAlert();

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderType, setOrderType] = useState("FARMER");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  const handleDownloadInvoice = (orderId) => {
    const orderToDownload = orders.find((order) => order.id === orderId);
    if (orderToDownload?.invoice_link) {
      window.open(orderToDownload.invoice_link, "_blank");
    } else {
      showAlert("error", "Invoice not available for this order");
    }
  };

  const handleSharePaymentLink = (orderId) => {
    sharePaymentLink(orderId, showAlert, t);
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const response = await api.get("/biochar/biochar-order/");
        const biocharOrders = response?.data?.data || [];
        setOrders(biocharOrders);
      } catch (error) {
        console.error("Error fetching orders:", error);
        showAlert("error", "Failed to load orders");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  const filteredOrders = Array.isArray(orders)
    ? orders.filter((order) => order.order_type === orderType)
    : [];

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setDetailsOpen(true);
  };

  const tabs = [
    { value: "FARMER", label: "Farmer" },
    { value: "INSTITUTION", label: "Institution" },
  ];

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <CustomAppBar />
      <Container maxWidth={isDesktop ? "lg" : "xs"}>
        <Box sx={{ mt: 3, mb: 4 }}>
          <CustomTitle>{t("Biochar Sale Orders")}</CustomTitle>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            {tabs.map((tab) => (
              <Grid item xs={isDesktop ? 2 : 6} key={tab.value}>
                <GenericButton
                  backgroundColor={
                    orderType === tab.value ? "primary" : "#e0e0e0"
                  }
                  fontColor={orderType === tab.value ? "#fff" : "#333"}
                  fontSize="14px"
                  padding="7px 10px"
                  fullWidth
                  onClick={() => setOrderType(tab.value)}
                >
                  {t(tab.label)}
                </GenericButton>
              </Grid>
            ))}
          </Grid>

          {isNotNullOrEmptyArray(filteredOrders) ? (
            isDesktop ? (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        {orderType === "FARMER"
                          ? "Farmer Name"
                          : "Institution Name"}
                      </TableCell>
                      <TableCell>Order ID</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell>Amount</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredOrders.map((order) => (
                      <OrderDesktopCard
                        key={order.id}
                        order={order}
                        onViewDetails={handleViewDetails}
                        customButtons={
                          order.order_status === "PAID"
                            ? [
                                {
                                  label: "Download Invoice",
                                  onClick: () =>
                                    handleDownloadInvoice(order.id),
                                  color: "secondary",
                                  disabled: !order.invoice_link,
                                },
                              ]
                            : order.order_status === "UNPAID"
                            ? [
                                {
                                  label: "Share Payment Link",
                                  onClick: () =>
                                    handleSharePaymentLink(order.id),
                                  color: "secondary",
                                },
                              ]
                            : []
                        }
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              filteredOrders.map((order) => (
                <OrderCard
                  key={order.id}
                  order={order}
                  onViewDetails={handleViewDetails}
                  customButtons={
                    order.order_status === "PAID"
                      ? [
                          {
                            label: "Download Invoice",
                            onClick: () => handleDownloadInvoice(order.id),
                            color: "secondary",
                            disabled: !order.invoice_link,
                          },
                        ]
                      : order.order_status === "UNPAID"
                      ? [
                          {
                            label: "Share Payment Link",
                            onClick: () => handleSharePaymentLink(order.id),
                            color: "secondary",
                          },
                        ]
                      : []
                  }
                />
              ))
            )
          ) : (
            <Box sx={{ textAlign: "center", marginTop: 3 }}>
              <Typography variant="body1">{t("No orders found")}</Typography>
            </Box>
          )}

          <OrderDetailsDialog
            open={detailsOpen}
            order={selectedOrder}
            onClose={() => setDetailsOpen(false)}
            customActions={null}
          />
        </Box>
      </Container>
    </>
  );
};

export default BiocharSaleOrders;
